import { format, isValid, parseISO } from "date-fns";

import { SupportRequest } from "@/app/entities/SupportRequest";
import masks from "@/app/utils/masks";

export interface SimulationRow {
  label: string;
  value: string;
}

export interface SimulationSection {
  title: string;
  rows: SimulationRow[];
}

type SectionId =
  | "personal"
  | "address"
  | "bank"
  | "simulation"
  | "references"
  | "vehicle";

// mesma ordem do cadastro do cliente; o resto vem depois
const SECTIONS: { id: SectionId; title: string }[] = [
  { id: "personal", title: "Dados pessoais" },
  { id: "address", title: "Endereço" },
  { id: "bank", title: "Dados bancários" },
  { id: "simulation", title: "Simulação" },
  { id: "references", title: "Referências" },
  { id: "vehicle", title: "Veículo" },
];

// o grupo pai decide a seção (address.number é endereço, rg.number é pessoal)
const SECTION_BY_PARENT: Record<string, SectionId> = {
  rg: "personal",
  placeOfBirth: "personal",
  income: "personal",
  cliente: "personal",
  address: "address",
  endereco: "address",
  bankAccount: "bank",
  bancario: "bank",
  consentBankAccount: "bank",
  proposal: "simulation",
  simulation: "simulation",
  operacao: "simulation",
  selectedOffer: "simulation",
  selectedCondition: "simulation",
  selectedCard: "simulation",
  referenceContact: "references",
  referencia: "references",
  vehicle: "vehicle",
};

// campos soltos (formulários planos, como a pré-análise da Crefaz CLT)
const SECTION_BY_KEY: Record<string, SectionId> = {
  cep: "address",
  zipCode: "address",
  zipcode: "address",
  logradouro: "address",
  street: "address",
  bairro: "address",
  neighborhood: "address",
  cidade: "address",
  city: "address",
  uf: "address",
  state: "address",
  complement: "address",
  complemento: "address",
  bank: "bank",
  bancoId: "bank",
  agency: "bank",
  agencia: "bank",
  agencyDigit: "bank",
  account: "bank",
  conta: "bank",
  accountDigit: "bank",
  accountType: "bank",
  tipoConta: "bank",
  requestedValue: "simulation",
  valorSolicitado: "simulation",
  financedValue: "simulation",
  installmentValue: "simulation",
  valorParcela: "simulation",
  installments: "simulation",
  prazo: "simulation",
  netAmount: "simulation",
  tableName: "simulation",
  tableCode: "simulation",
  matricula: "simulation",
  simulationMode: "simulation",
  dueDate: "simulation",
  vencimento: "simulation",
  authorized: "simulation",
  valorBeneficio: "simulation",
  possuiEmprestimo: "simulation",
  valorEmprestimoAtivo: "simulation",
  recebeViaCrefisa: "simulation",
  naoRecebeViaCrefisa: "simulation",
  possuiDebitos: "simulation",
  valorTotalDebitos: "simulation",
  borrowedValue: "simulation",
  isRefinancing: "simulation",
  status: "simulation",
  statusEsteira: "simulation",
  plate: "vehicle",
  year: "vehicle",
  typeVehicle: "vehicle",
};

const LABELS: Record<string, string> = {
  // pessoais
  cpf: "CPF",
  customerCpf: "CPF",
  name: "Nome",
  nome: "Nome",
  customerName: "Cliente",
  sellerName: "Vendedor",
  teamName: "Equipe",
  email: "E-mail",
  phone: "Telefone",
  telefone: "Telefone",
  ddd: "DDD",
  birthDate: "Data de nascimento",
  dataNascimento: "Data de nascimento",
  motherName: "Nome da mãe",
  nomeMae: "Nome da mãe",
  fatherName: "Nome do pai",
  spouseName: "Nome do cônjuge",
  nomeConjuge: "Nome do cônjuge",
  maritalStatus: "Estado civil",
  estadoCivil: "Estado civil",
  gender: "Sexo",
  sexo: "Sexo",
  educationLevel: "Escolaridade",
  ocupation: "Ocupação",
  diaRecebimento: "Dia de recebimento",
  pep: "Pessoa politicamente exposta",
  authorizeSMS: "Autoriza SMS",
  hasVisualImpairment: "Deficiência visual",
  rg: "RG",
  rgEmissor: "Órgão emissor do RG",
  rgUf: "UF do RG",
  rgEmissao: "Emissão do RG",
  org: "Órgão emissor",
  date: "Data de emissão",
  placeOfBirth: "Naturalidade",
  registration: "Matrícula",
  registrationDigit: "Dígito da matrícula",
  valueIncome: "Valor da renda",
  // endereço
  cep: "CEP",
  zipCode: "CEP",
  zipcode: "CEP",
  street: "Logradouro",
  logradouro: "Logradouro",
  number: "Número",
  numero: "Número",
  complement: "Complemento",
  complemento: "Complemento",
  neighborhood: "Bairro",
  bairro: "Bairro",
  city: "Cidade",
  cidade: "Cidade",
  state: "Estado",
  uf: "UF",
  // bancários
  bank: "Banco",
  bancoId: "Banco",
  agency: "Agência",
  agencia: "Agência",
  agencyDigit: "Dígito da agência",
  account: "Conta",
  conta: "Conta",
  accountDigit: "Dígito da conta",
  digito: "Dígito da conta",
  accountType: "Tipo de conta",
  tipoConta: "Tipo de conta",
  // simulação
  requestedValue: "Valor solicitado",
  valorSolicitado: "Valor solicitado",
  valorLiberado: "Valor liberado",
  financedValue: "Valor financiado",
  installmentValue: "Valor da parcela",
  valorParcela: "Valor da parcela",
  prestacao: "Valor da parcela",
  installments: "Parcelas",
  prazo: "Parcelas",
  netAmount: "Valor líquido",
  value: "Valor",
  valor: "Valor",
  status: "Status",
  statusEsteira: "Status na esteira",
  tableName: "Tabela",
  tableCode: "Código da tabela",
  dueDate: "Vencimento",
  vencimento: "Vencimento",
  matricula: "Matrícula",
  simulationMode: "Modo de simulação",
  authorized: "Autorizado",
  propostaId: "Proposta",
  contract: "Contrato",
  valorBeneficio: "Valor do benefício",
  possuiEmprestimo: "Possui empréstimo",
  valorEmprestimoAtivo: "Valor do empréstimo ativo",
  recebeViaCrefisa: "Recebe via Crefisa",
  naoRecebeViaCrefisa: "Não recebe via Crefisa",
  possuiDebitos: "Possui débitos",
  valorTotalDebitos: "Valor total dos débitos",
  borrowedValue: "Valor emprestado",
  isRefinancing: "Refinanciamento",
  createdAt: "Data do cadastro",
  updatedAt: "Data da atualização",
  // referências
  degree: "Grau de parentesco",
  grau: "Grau de parentesco",
  // veículo
  vehicle: "Veículo",
  plate: "Placa",
  year: "Ano",
  typeVehicle: "Tipo de veículo",
  description: "Descrição",
};

const MONEY_KEYS = new Set([
  "requestedValue",
  "valorSolicitado",
  "valorLiberado",
  "financedValue",
  "installmentValue",
  "valorParcela",
  "prestacao",
  "netAmount",
  "renda",
  "valueIncome",
  "valorBeneficio",
  "valorEmprestimoAtivo",
  "valorTotalDebitos",
  "borrowedValue",
  "value",
  "valor",
]);

const DATE_KEYS = new Set([
  "birthDate",
  "dataNascimento",
  "date",
  "rgEmissao",
  "dueDate",
  "vencimento",
  "createdAt",
  "updatedAt",
]);

// ids internos, listas de ofertas e campos de controle não ajudam o suporte
const HIDDEN_KEYS = new Set([
  "id",
  "_id",
  "password",
  "token",
  "message",
  "offers",
  "preApproveOffers",
  "conditions",
  "type",
  "__v",
  "_v",
  "isClient",
  "isAutorized",
  "isAuthorized",
  "isContacted",
  "isArchived",
  "renda",
  "amount",
]);

// propostaId e bancoId são dados de negócio, não ids internos
const VISIBLE_ID_KEYS = new Set(["propostaId", "bancoId"]);

const isHiddenKey = (key: string) =>
  HIDDEN_KEYS.has(key) || (/Id$/.test(key) && !VISIBLE_ID_KEYS.has(key));

const MAX_ROWS_PER_SECTION = 30;

const formatValue = (key: string, value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "boolean") return value ? "Sim" : "Não";

  const lower = key?.toLowerCase();

  if (MONEY_KEYS.has(key)) {
    const amount = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(amount)) {
      return amount.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    }
  }

  const text = String(value);

  if (DATE_KEYS.has(key) && /^\d{4}-\d{2}-\d{2}/.test(text)) {
    const parsed = parseISO(text);
    if (isValid(parsed)) return format(parsed, "dd/MM/yyyy");
  }

  const digits = text.replace(/\D/g, "");

  if (lower?.includes("cpf") && digits.length === 11) return masks.cpf(digits);
  if (
    (lower?.includes("phone") || lower?.includes("telefone")) &&
    digits.length >= 10
  )
    return masks.phone(digits);
  if ((key === "cep" || lower === "zipcode") && digits.length === 8)
    return masks.zipcode(digits);

  return text;
};

const sectionOf = (path: string[]): SectionId => {
  for (const segment of path.slice(0, -1)) {
    if (SECTION_BY_PARENT[segment]) return SECTION_BY_PARENT[segment];
  }
  return SECTION_BY_KEY[path[path.length - 1]] ?? "personal";
};

// campos de subgrupos que, sozinhos, ficariam ambíguos na mesma seção
const NESTED_LABELS: Record<string, Record<string, string>> = {
  rg: {
    number: "RG",
    uf: "UF do RG",
    org: "Órgão emissor do RG",
    date: "Data de emissão do RG",
  },
  placeOfBirth: { city: "Naturalidade", uf: "UF de naturalidade" },
};

// o rótulo é só o do campo; referências levam o número para separar as pessoas
const labelOf = (path: string[]) => {
  const key = path[path.length - 1];
  const parents = path.slice(0, -1);
  const parent = parents[parents.length - 1];

  const label = NESTED_LABELS[parent]?.[key] ?? LABELS[key] ?? key;

  const referenceIndex = parents.findIndex(
    (segment) => segment === "referenceContact" || segment === "referencia",
  );
  if (referenceIndex >= 0 && parents[referenceIndex + 1]) {
    return `Referência ${parents[referenceIndex + 1]} — ${label}`;
  }

  return label;
};

const collect = (
  value: unknown,
  path: string[],
  sections: Map<SectionId, SimulationRow[]>,
  seen: Set<string>,
) => {
  if (Array.isArray(value)) {
    value
      .slice(0, 5)
      .forEach((item, index) =>
        collect(item, [...path, String(index + 1)], sections, seen),
      );
    return;
  }

  if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => {
      if (!isHiddenKey(key)) collect(item, [...path, key], sections, seen);
    });
    return;
  }

  const key = path[path.length - 1];
  const formatted = formatValue(key, value);
  if (!formatted) return;

  const sectionId = sectionOf(path);
  const label = labelOf(path);

  // o mesmo dado costuma vir de mais de uma etapa (ex.: CPF)
  const dedupeKey = `${sectionId}:${label}:${formatted}`;
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);

  const rows = sections.get(sectionId) ?? [];
  if (rows.length >= MAX_ROWS_PER_SECTION) return;
  sections.set(sectionId, [...rows, { label, value: formatted }]);
};

/**
 * O app manda `metadata.flow/payload/customer`; o webapp manda `metadata.context`
 * com o produto, a etapa e os dados da tela onde o chamado foi aberto.
 */
export const resolveSimulation = (
  data: SupportRequest,
): { step?: string; sections: SimulationSection[] } | null => {
  const { flow, payload, customer, context } = data.metadata ?? {};

  const sections = new Map<SectionId, SimulationRow[]>();
  const seen = new Set<string>();

  collect(customer, [], sections, seen);
  collect(payload, [], sections, seen);
  collect(context?.data, [], sections, seen);

  const step =
    context?.step ??
    (typeof flow === "string" ? flow.replace(/_/g, " ") : undefined);

  const ordered = SECTIONS.filter(({ id }) => sections.has(id)).map(
    ({ id, title }) => ({ title, rows: sections.get(id)! }),
  );

  if (!ordered.length && !step) return null;

  return { step, sections: ordered };
};
